/**
 * Site status — whether a property's network is up.
 *
 * Devices join and leave all day. An access point in a closed outlet does not
 * mean the hotel is down. The colour answers one question: is the network up?
 * WAN uptime, when UniFi has reported it, is the number on the card — how long
 * the circuit has been up — and it does not change the colour.
 *
 *   • A gateway that is still answering means the site is live.
 *   • The site is down only when every gateway is offline, or — when no
 *     gateway was reported — when nothing on the network is answering.
 *   • Stale or missing telemetry never diagnoses. It reads as not monitored.
 */

export type SiteState = 'critical' | 'serious' | 'warning' | 'good' | 'unknown';

/** Worst first, so a down network is the first cell an operator sees. */
export const STATE_ORDER: Record<SiteState, number> = {
  critical: 0,
  serious: 1,
  warning: 2,
  unknown: 3,
  good: 4,
};

export interface SiteTelemetry {
  totalDevices: number | null;
  onlineDevices: number | null;
  offlineDevices: number | null;
  /** Adopted gateways. Null when the poll did not report a gateway count. */
  gatewayDevices?: number | null;
  offlineGatewayDevices?: number | null;
  latestUpdatedAt: string | null;
}

export interface SiteSignals {
  telemetry: SiteTelemetry | null;
}

export type TelemetryState = 'live' | 'stale' | 'none';

export interface SiteStatus {
  state: SiteState;
  hasTelemetry: boolean;
  telemetryState: TelemetryState;
  telemetryAgeMs: number | null;
}

function networkVerdict(telemetry: SiteTelemetry): 'up' | 'down' | 'unknown' {
  const gateways = telemetry.gatewayDevices ?? null;
  const gatewaysOffline = telemetry.offlineGatewayDevices ?? 0;
  if (gateways !== null && gateways > 0) {
    return gatewaysOffline >= gateways ? 'down' : 'up';
  }
  const total = telemetry.totalDevices ?? 0;
  if (total <= 0) return 'unknown';
  return (telemetry.onlineDevices ?? 0) > 0 ? 'up' : 'down';
}

/**
 * @param staleMs how old telemetry may be before it stops describing the site
 * @param now injectable for tests
 */
export function deriveSiteStatus(
  signals: SiteSignals,
  staleMs: number,
  now: number = Date.now(),
): SiteStatus {
  const telemetry = signals.telemetry;
  const ageMs = telemetry?.latestUpdatedAt
    ? now - new Date(telemetry.latestUpdatedAt).getTime()
    : Number.POSITIVE_INFINITY;

  const everReported = !!telemetry && telemetry.totalDevices !== null && Number.isFinite(ageMs);
  const hasTelemetry = everReported && ageMs <= staleMs;
  const telemetryState: TelemetryState = hasTelemetry ? 'live' : everReported ? 'stale' : 'none';

  let state: SiteState = 'unknown';
  if (hasTelemetry && telemetry) {
    const verdict = networkVerdict(telemetry);
    if (verdict === 'down') state = 'critical';
    else if (verdict === 'up') state = 'good';
  }

  return {
    state,
    hasTelemetry,
    telemetryState,
    telemetryAgeMs: everReported ? ageMs : null,
  };
}

/** A site is an exception only when its network is down. */
export function isException(state: SiteState): boolean {
  return state === 'critical';
}

export function compareSiteState(a: SiteState, b: SiteState): number {
  return STATE_ORDER[a] - STATE_ORDER[b];
}
